/*
 * Infinite loading for the collection product grid.
 *
 * Replaces the numbered pagination on the PLP: the element sits below the grid and tracks how
 * far the shopper has scrolled through the products already loaded, appending the next page
 * once they are halfway through, until the collection is exhausted.
 *
 * The element is rendered inside #ProductGridContainer, so a filter or sort change --
 * which replaces that container's markup wholesale -- discards it and connects a fresh
 * one pointed at the first page of the new result set.
 */
/*
 * Both thresholds are a fraction of the products currently in the grid, not a distance from
 * the bottom, so the trigger scales with however much is loaded. Appending at the halfway
 * mark leaves the shopper the other half still to scroll while the next page arrives, and
 * because the grid grows with every append the ratio drops back below the threshold each
 * time -- there is always about as much grid ahead of them as they have already been through.
 */
const APPEND_AT_RATIO = 0.5;
const PREFETCH_AT_RATIO = 0.25;

class ProductGridInfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.loadingElement = this.querySelector('.infinite-scroll__loading');
    this.endElement = this.querySelector('.infinite-scroll__end');
    this.loading = false;
    this.failures = 0;
    this.prefetch = null;
    this.evaluationQueued = false;
    this.onScroll = this.scheduleEvaluation.bind(this);
  }

  connectedCallback() {
    if (!this.nextUrl) {
      this.markComplete();
      return;
    }

    this.abortController = new AbortController();

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    /*
     * A shopper who never scrolls never needs the next page, and these section renders are
     * large, so the warm-up holds off until there is some movement. The append threshold is
     * deliberately not gated this way: a grid that is already more than half read on arrival
     * -- a short filtered result, say -- still fills itself.
     */
    this.onFirstScroll = () => {
      this.scrolled = true;
    };
    window.addEventListener('scroll', this.onFirstScroll, { once: true, passive: true });

    this.scheduleEvaluation();
  }

  disconnectedCallback() {
    this.stopWatching();
    this.prefetch = null;
    if (this.abortController) this.abortController.abort();
  }

  /*
   * How much of the loaded grid the shopper has been through, measured at the bottom edge of
   * the viewport: 0 while the first row is coming into view, 1 once the last row is reached.
   */
  get scrolledRatio() {
    const grid = this.grid;
    if (!grid) return 0;

    const rect = grid.getBoundingClientRect();
    if (rect.height <= 0) return 0;

    return Math.min(Math.max((this.viewportHeight - rect.top) / rect.height, 0), 1);
  }

  get viewportHeight() {
    return window.innerHeight || document.documentElement.clientHeight;
  }

  /* Scroll events fire far faster than layout needs reading; collapse them into a frame. */
  scheduleEvaluation() {
    if (this.evaluationQueued) return;
    this.evaluationQueued = true;

    requestAnimationFrame(() => {
      this.evaluationQueued = false;
      this.evaluatePosition();
    });
  }

  evaluatePosition() {
    if (!this.isConnected || this.loading || !this.nextUrl) return;

    const ratio = this.scrolledRatio;
    if (ratio >= APPEND_AT_RATIO) {
      this.loadNextPage();
    } else if (ratio >= PREFETCH_AT_RATIO) {
      this.prefetchNextPage();
    }
  }

  get nextUrl() {
    return this.dataset.nextUrl || '';
  }

  get grid() {
    return ProductGridInfiniteScroll.resolveGrid(document);
  }

  /*
   * The collection grid carries id="product-grid" on the <ul> itself, while the search grid
   * carries it on the wrapper <div> around the list. Resolve the list for either template,
   * and scope the lookup to #ProductGridContainer so unrelated product grids elsewhere on
   * the page -- the "Pairs great with" slider on search, for one -- are never picked up.
   */
  static resolveGrid(root) {
    const marked = root.getElementById('product-grid');
    if (marked && marked.tagName === 'UL') return marked;

    const container = root.getElementById('ProductGridContainer') || marked;
    return container ? container.querySelector('ul.product-grid') : null;
  }

  fetchPage(url) {
    return fetch(this.sectionUrl(url), { signal: this.abortController.signal }).then((response) => {
      if (!response.ok) throw new Error(`Product grid page request failed with ${response.status}`);
      return response.text();
    });
  }

  /*
   * Warms the next page into memory without touching the DOM. A failed warm-up is discarded
   * rather than counted against `failures`: loadNextPage will simply request the page again
   * when it is actually needed.
   */
  prefetchNextPage() {
    if (!this.scrolled || !this.nextUrl || this.failures > 0) return;
    if (this.prefetch && this.prefetch.url === this.nextUrl) return;

    const record = { url: this.nextUrl, settled: false };
    record.request = this.fetchPage(record.url)
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
        if (this.prefetch === record) this.prefetch = null;
        return null;
      })
      .then((html) => {
        record.settled = true;
        return html;
      });

    this.prefetch = record;
  }

  takePrefetched(url) {
    if (!this.prefetch || this.prefetch.url !== url) return null;
    const record = this.prefetch;
    this.prefetch = null;
    return record;
  }

  async loadNextPage() {
    if (this.loading || !this.nextUrl) return;

    const url = this.nextUrl;
    const warmed = this.takePrefetched(url);
    this.loading = true;
    // Nothing to wait for when the page is already in hand, so no spinner flashes past.
    if (!warmed || !warmed.settled) this.toggleLoading(true);

    try {
      const html = (warmed ? await warmed.request : null) || (await this.fetchPage(url));

      // A filter change can swap the grid out while the request is in flight; those
      // products belong to the previous result set, so drop them.
      if (!this.isConnected) return;

      const page = new DOMParser().parseFromString(html, 'text/html');
      const appendedItems = this.appendProducts(page);
      this.failures = 0;
      this.setNextUrl(page);
      if (appendedItems.length) this.initializeProducts(appendedItems);

      // The shopper is moving through the grid, so start on the page after this one now.
      this.prefetchNextPage();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      this.failures += 1;
      // Keep the next URL so a later scroll can retry, but stop hammering the server
      // once a page has failed repeatedly.
      if (this.failures >= 3) this.stopWatching();
    } finally {
      this.loading = false;
      this.toggleLoading(false);
    }

    /*
     * Re-measure after appending: a page whose products were all hidden by the grid's own
     * rules adds no height, so the ratio can still be past the threshold and the next page
     * is needed straight away. A failed load waits for the shopper to scroll again instead,
     * rather than retrying in a tight loop.
     */
    if (this.failures === 0) this.scheduleEvaluation();
  }

  appendProducts(page) {
    const grid = this.grid;
    const sourceGrid = ProductGridInfiniteScroll.resolveGrid(page);
    if (!grid || !sourceGrid || !sourceGrid.children.length) return [];

    // Stage the items in the current document first: initializeScrollAnimationTrigger
    // needs an element root, and observing before the append keeps the reveal animation
    // of the new cards intact without re-observing the cards already on screen.
    const stage = document.createElement('div');
    Array.from(sourceGrid.children).forEach((item) => stage.appendChild(item));
    const items = Array.from(stage.children);

    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(stage);

    while (stage.firstElementChild) grid.appendChild(stage.firstElementChild);

    this.removeEmptyState();

    return items;
  }

  /*
   * The grid renders its "no products found" message when every product on the first
   * page was hidden (sale-price rule, gift products). Later pages can still hold
   * products, so drop the message as soon as any card lands in the grid.
   */
  removeEmptyState() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    container.querySelectorAll('.collection__empty-message').forEach((element) => element.remove());
  }

  setNextUrl(page) {
    const nextElement = page.querySelector('product-grid-infinite-scroll');
    const nextUrl = nextElement ? nextElement.dataset.nextUrl : '';

    if (nextUrl) {
      this.dataset.nextUrl = this.normalizeUrl(nextUrl);
    } else {
      delete this.dataset.nextUrl;
      this.markComplete();
    }
  }

  initializeProducts(items) {
    // Binds the swatch, quick add and mobile quick add handlers. It skips the cards it
    // has already bound, so calling it per page does not double up the existing ones.
    if (typeof cardProductAddToCartFunction === 'function') cardProductAddToCartFunction();
    this.selectFilteredColorSwatches(items);
  }

  /*
   * Mirrors the on-load behaviour of a colour-filtered collection: each card opens on the
   * colour the shopper filtered by, rather than on its default colour.
   */
  selectFilteredColorSwatches(items) {
    const colorValues = new URLSearchParams(window.location.search).getAll('filter.v.option.color');
    if (!colorValues.length) return;

    items.forEach((item) => {
      const swatches = Array.from(item.querySelectorAll('.card-product-swatch-button'));
      if (!swatches.length) return;

      for (const colorValue of colorValues) {
        const match = swatches.find((swatch) => swatch.dataset.colorValue === colorValue);
        const target = match && match.querySelector('span.card-product-color-swatch');
        if (target) {
          target.click();
          break;
        }
      }
    });
  }

  markComplete() {
    this.stopWatching();
    if (this.endElement) this.endElement.classList.remove('hidden');
  }

  stopWatching() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.onFirstScroll) window.removeEventListener('scroll', this.onFirstScroll);
  }

  toggleLoading(isLoading) {
    if (this.loadingElement) this.loadingElement.classList.toggle('hidden', !isLoading);
  }

  /* Strips the Section Rendering API parameter Liquid carries over into paginate.next.url. */
  normalizeUrl(path) {
    const url = new URL(path, window.location.origin);
    url.searchParams.delete('section_id');
    return `${url.pathname}${url.search}`;
  }

  sectionUrl(path) {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId);
    return `${url.pathname}${url.search}`;
  }
}

customElements.define('product-grid-infinite-scroll', ProductGridInfiniteScroll);
